'use client';

import { useId } from 'react';
import { Switch } from '@1debit/chimekit';
import Button from 'components/shared/Buttons/BaseButton';
import { CALCULATOR_RESULT_CARD } from 'components/Widget/shared/cardStyles';
import { formatCompactCurrency, formatCurrency, toCents } from 'components/Widget/shared/currency';
import { getEnrollmentUrl } from 'utils/linkHelpers';
import { cn } from 'utils/styles';
import {
  CURRENT_PLAN_HEADING,
  EXTRA_PAYMENT_FIELD,
  formatDuration,
  PILL_ROWS,
  RESULT_COPY,
  WITH_EXTRA_PAYMENT_HEADING,
} from './constants';
import type { LoanPanel, LoanScenario } from './types';

interface LoanCalculatorResultProps {
  panel: LoanPanel;
  showComparison: boolean;
  onToggleComparison: (value: boolean) => void;
}

const RAISED = 'align-super text-heading-sm';

const LABEL = 'text-body-saans text-content-secondary';

/** Avoid `<sup>` because global styles and assistive technology treat it as a footnote marker. */
const HeroAmount = ({ value }: { value: number }) => {
  const cents = toCents(value);
  const dollars = Math.trunc(cents / 100).toLocaleString('en-US');

  return (
    <p className="text-heading-lg">
      <span className="sr-only">{formatCurrency(value)}</span>
      <span aria-hidden>
        <span className={RAISED}>$</span>
        {dollars}.<span className={RAISED}>{String(cents % 100).padStart(2, '0')}</span>
      </span>
    </p>
  );
};

interface PillValueProps {
  scenario: LoanScenario;
  pill: (typeof PILL_ROWS)[number];
  columnLabel?: string;
}

/** Compact on screen with the exact figure beside it, so a `$183K` pill is never the only number read out. */
const PillValue = ({ scenario, pill, columnLabel }: PillValueProps) => {
  const accessibleColumnLabel = columnLabel && <span className="sr-only">{columnLabel}: </span>;

  if (pill.key === 'years') {
    return (
      <span className="text-heading-sm">
        {accessibleColumnLabel}
        {formatDuration(scenario.monthsToPayoff)}
      </span>
    );
  }

  const value = scenario[pill.key];

  return (
    <span className="text-heading-sm">
      {accessibleColumnLabel}
      <span className="sr-only">{formatCurrency(value)}</span>
      <span aria-hidden>{formatCompactCurrency(value)}</span>
    </span>
  );
};

export const LoanCalculatorResult = ({ panel, showComparison, onToggleComparison }: LoanCalculatorResultProps) => {
  const switchId = useId();
  const { result } = panel;
  const extraPayment = formatCurrency(result.extraPayment);
  const interestSaved = formatCurrency(result.interestSaved);

  return (
    <div className={CALCULATOR_RESULT_CARD}>
      <div>
        <p className={LABEL}>{RESULT_COPY.heroEyebrow}</p>
        <HeroAmount value={result.baseline.monthlyPayment} />
      </div>

      <div className="flex flex-col gap-4">
        {/* Always laid out, only revealed with the comparison: inserting it would push every pill down. */}
        <div
          aria-hidden
          className={cn('grid grid-cols-2 gap-4 text-legal text-content-secondary uppercase', {
            invisible: !showComparison,
          })}>
          <p>{CURRENT_PLAN_HEADING}</p>
          <p>{WITH_EXTRA_PAYMENT_HEADING}</p>
        </div>

        <dl className="flex flex-col gap-4">
          {PILL_ROWS.map(pill => (
            <div key={pill.key} className="flex flex-col gap-1">
              <dt className={LABEL}>{pill.label}</dt>
              <dd className={cn('grid gap-4', showComparison ? 'grid-cols-2' : 'grid-cols-1')}>
                <PillValue
                  scenario={result.baseline}
                  pill={pill}
                  columnLabel={showComparison ? CURRENT_PLAN_HEADING : undefined}
                />
                {showComparison && (
                  <PillValue scenario={result.withExtra} pill={pill} columnLabel={WITH_EXTRA_PAYMENT_HEADING} />
                )}
              </dd>
            </div>
          ))}
        </dl>
      </div>

      {/* Below the pills rather than above them, so using it does not move what it changes. */}
      <div className="flex items-center gap-3">
        <Switch
          id={switchId}
          value={showComparison}
          onValueChange={onToggleComparison}
          aria-labelledby={`${switchId}-label`}
        />
        <label id={`${switchId}-label`} htmlFor={switchId} className="text-body-saans">
          {EXTRA_PAYMENT_FIELD.label} (optional) - {RESULT_COPY.comparisonToggleAmount(extraPayment)}
        </label>
      </div>

      <p className={LABEL}>
        {showComparison
          ? RESULT_COPY.comparison(extraPayment, interestSaved, result.monthsSaved)
          : RESULT_COPY.teaser(extraPayment, interestSaved, result.monthsSaved)}
      </p>

      <div className="flex flex-col gap-2">
        <p className="text-label-saans">{RESULT_COPY.ctaHeading}</p>
        <p className={LABEL}>{RESULT_COPY.ctaBody}</p>
        {/* `Button href` already fires the enrollment CTA event through `Link`; a handler here double-counts. */}
        <Button href={getEnrollmentUrl()} className="mt-2 self-start">
          {RESULT_COPY.ctaLabel}
        </Button>
      </div>
    </div>
  );
};
